import React, { useEffect } from 'react';
import { FormHelperText, TextField } from '@material-ui/core';

import { CategoryType, ManufacturerType, ModelType, useFipe, VehicleType } from '~/services/hooks/useFipe';
import { formatLicensePlate, onlyNumbers } from '~/util/format';
import { useAssociations } from '~/services/hooks';
import { Divider, TitleModal } from '~/pages/NewOpportunity/styles';
import { Association } from '~/types';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import Loading from '~/components/Loading';
import { useIBGE } from '~/services/hooks/useIBGE';

interface NewVehicleProps {
  modalVehicleIsOpen: boolean;
  fipeModel: FipeModel;
  setFipeModel: (value: FipeModel) => void;
  showStatus?: boolean;
  initialData?: VehicleInitialData;
  title?: string;
}

export interface FipeModel {
  association?: Association;
  association_error?: boolean;
  status?: VehicleStatusType;
  status_error?: boolean;
  category?: CategoryType;
  category_error?: boolean;
  manufacturer?: ManufacturerType;
  manufacturer_value?: string;
  manufacturer_error?: boolean;
  vehicle?: VehicleType;
  vehicle_value?: string;
  vehicle_error?: boolean;
  model?: ModelType;
  model_value?: string;
  model_error?: boolean;
  license_plate?: string;
  license_plate_error?: boolean;
  vin?: string;
  color?: string;
  color_error?: boolean;
  city?: string;
  city_error?: boolean;
  state?: string;
  state_error?: boolean;
}

export type SelectedBenefits = Record<string, string>;

export type VehicleInitialData = {
  vehicleId?: string;
  associationId?: string;
  categoryId?: string;
  manufacturerDescription?: string;
  vehicleDescription?: string;
  modelDescription?: string;
  licensePlate?: string;
  vin?: string;
  color?: string;
  state?: string;
  city?: string;
  status?: string;
};

export type VehicleStatusType = keyof typeof VehicleStatus | undefined;

export const VehicleStatus = {
  ACTIVE: 'ATIVO',
  INACTIVE: 'INATIVO',
  SUSPENDED: 'SUSPENSO',
  PENDING: 'PENDENTE',
};

export function VehicleRegistration({
  modalVehicleIsOpen,
  fipeModel,
  setFipeModel,
  showStatus,
  initialData,
  title = 'Novo veículo',
}: NewVehicleProps) {
  const {
    categoryTypes: fipeTypes,
    loadingCategory: loadingFipe,
    manufacturerTypes,
    loadingManufacturer,
    loadingVehicles: loadingVehiclesFipe,
    vehiclesTypes,
    modelsTypes,
    loadingModels,
    isSpecialVehicle,
  } = useFipe({
    getInfo: modalVehicleIsOpen,
    category: fipeModel.category,
    manufacturer: fipeModel.manufacturer,
    vehicle: fipeModel.vehicle,
  });
  const { associations } = useAssociations();
  const { loadingStates, loadingCities, states, cities } = useIBGE({ stateId: fipeModel.state });

  const handleChangeSelect = (type: keyof typeof fipeModel, value?: string, upperCase = false) => {
    const newValue =
      type === 'model' && isSpecialVehicle(fipeModel.category?.description)
        ? onlyNumbers(value?.toUpperCase())
        : type === 'license_plate'
        ? formatLicensePlate(value)
        : upperCase
        ? value?.toUpperCase()
        : value;

    switch (type) {
      case 'category':
        setFipeModel({
          ...fipeModel,
          [type]: newValue,
          [`${type}_error`]: false,
          manufacturer: undefined,
          manufacturer_error: false,
          vehicle: undefined,
          vehicle_error: false,
          model: undefined,
          model_error: false,
        });
        break;

      case 'manufacturer':
        setFipeModel({
          ...fipeModel,
          [type]: newValue,
          [`${type}_error`]: false,
          vehicle: undefined,
          vehicle_error: false,
          model: undefined,
          model_error: false,
        });
        break;

      case 'vehicle':
        setFipeModel({
          ...fipeModel,
          [type]: newValue,
          [`${type}_error`]: false,
          model: undefined,
          model_error: false,
        });
        break;

      case 'state':
        setFipeModel({ ...fipeModel, [type]: newValue, [`${type}_error`]: false, city: '', city_error: true });
        break;

      default:
        setFipeModel({ ...fipeModel, [type]: newValue, [`${type}_error`]: false });
        break;
    }
  };

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const associationFound = associations.find(association => association.id === initialData.associationId);

    setFipeModel({
      ...fipeModel,
      association: associationFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associations, initialData, setFipeModel]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const categoryFound = fipeTypes.find(category => category.id === initialData.categoryId);

    setFipeModel({
      ...fipeModel,
      category: categoryFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fipeTypes, initialData, setFipeModel]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const manufacturerFound = manufacturerTypes.find(
      manufacture => manufacture.name.toUpperCase() === initialData.manufacturerDescription?.toUpperCase(),
    );

    setFipeModel({
      ...fipeModel,
      manufacturer: manufacturerFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturerTypes, initialData, setFipeModel]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const vehicleFound = vehiclesTypes.find(
      vehicle => vehicle.name.toUpperCase() === initialData.vehicleDescription?.toUpperCase(),
    );

    setFipeModel({
      ...fipeModel,
      vehicle: vehicleFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesTypes, initialData, setFipeModel]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const modelFound = modelsTypes.find(
      model => model.name.toUpperCase() === initialData.modelDescription?.toUpperCase(),
    );
    setFipeModel({
      ...fipeModel,
      model: modelFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsTypes, initialData, setFipeModel]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const statusFound = VehicleStatus[initialData.status || VehicleStatus.ACTIVE];

    setFipeModel({
      ...fipeModel,
      license_plate: initialData.licensePlate,
      vin: initialData.vin,
      color: initialData.color,
      state: initialData.state,
      city: initialData.city,
      status: statusFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, setFipeModel]);

  if (loadingFipe || loadingStates) {
    return <Loading />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <TitleModal>{title}</TitleModal>
      <Divider height={15} />

      <CustomAutocomplete
        id='combo-box-associations'
        options={associations}
        getOptionLabel={association => association.code}
        renderInput={params => <TextField {...params} label='Empresa' />}
        onChange={(_, value) => {
          setFipeModel({ ...fipeModel, association: value ?? undefined, association_error: false });
        }}
        value={fipeModel.association ?? null}
        width={300}
      />
      <FormHelperText error>{fipeModel.association_error ? 'Empresa obrigatória' : undefined}</FormHelperText>

      {showStatus && (
        <>
          <CustomAutocomplete
            id='combo-box-status'
            options={Object.values(VehicleStatus)}
            renderInput={params => <TextField {...params} label='Situação' />}
            onChange={(_, value) => {
              setFipeModel({ ...fipeModel, status: (value as VehicleStatusType) ?? undefined, status_error: false });
            }}
            value={fipeModel.status ?? null}
            width={300}
          />
          <FormHelperText error>{fipeModel.status_error ? 'Situação obrigatória' : undefined}</FormHelperText>
        </>
      )}

      <CustomAutocomplete
        id='combo-box-category'
        options={fipeTypes}
        getOptionLabel={type => type.description}
        renderInput={params => <TextField {...params} label='Categoria' />}
        onChange={(_, value) => {
          setFipeModel({
            ...fipeModel,
            category: value ?? undefined,
            category_error: false,
            manufacturer: undefined,
            manufacturer_error: false,
            vehicle: undefined,
            vehicle_error: false,
            model: undefined,
            model_error: false,
          });
        }}
        value={fipeModel.category ?? null}
        width={300}
      />
      <FormHelperText error>{fipeModel.category_error ? 'Categoria obrigatória' : undefined}</FormHelperText>

      {isSpecialVehicle(fipeModel.category?.description) ? (
        <TextField
          id='manufacturer-field'
          label='Marca'
          value={fipeModel.manufacturer}
          onChange={({ target }) => {
            handleChangeSelect('manufacturer', target.value, true);
          }}
          error={fipeModel.manufacturer_error}
          helperText={fipeModel.manufacturer_error ? 'Marca obrigatória' : ''}
        />
      ) : loadingManufacturer ? (
        <Loading />
      ) : (
        <>
          <CustomAutocomplete
            id='combo-box-manufacturer'
            options={manufacturerTypes}
            getOptionLabel={type => type.name}
            renderInput={params => <TextField {...params} label='Marca' />}
            onChange={(_, value) => {
              setFipeModel({
                ...fipeModel,
                manufacturer: value ?? undefined,
                manufacturer_error: false,
                vehicle: undefined,
                vehicle_error: false,
                model: undefined,
                model_error: false,
              });
            }}
            value={fipeModel.manufacturer ?? null}
            width={300}
          />
          <FormHelperText error>{fipeModel.manufacturer_error ? 'Marca obrigatória' : undefined}</FormHelperText>
        </>
      )}

      {isSpecialVehicle(fipeModel.category?.description) ? (
        <TextField
          id='vehicle-field'
          label='Modelo'
          value={fipeModel.vehicle}
          onChange={({ target }) => {
            handleChangeSelect('vehicle', target.value, true);
          }}
          error={fipeModel.vehicle_error}
          helperText={fipeModel.vehicle_error ? 'Modelo obrigatório' : ''}
        />
      ) : loadingVehiclesFipe ? (
        <Loading />
      ) : (
        <>
          <CustomAutocomplete
            id='combo-box-vehicle'
            options={vehiclesTypes}
            getOptionLabel={type => type.name}
            renderInput={params => <TextField {...params} label='Modelo' />}
            onChange={(_, value) => {
              setFipeModel({
                ...fipeModel,
                vehicle: value ?? undefined,
                vehicle_error: false,
                model: undefined,
                model_error: false,
              });
            }}
            value={fipeModel.vehicle ?? null}
            width={300}
          />
          <FormHelperText error>{fipeModel.vehicle_error ? 'Modelo obrigatório' : undefined}</FormHelperText>
        </>
      )}

      {isSpecialVehicle(fipeModel.category?.description) ? (
        <TextField
          id='year-field'
          label='Ano'
          value={fipeModel.model}
          onChange={({ target }) => {
            handleChangeSelect('model', target.value, true);
          }}
          error={fipeModel.model_error}
          helperText={fipeModel.model_error ? 'Ano obrigatório' : ''}
          inputProps={{ maxLength: 4 }}
        />
      ) : loadingModels ? (
        <Loading />
      ) : (
        <>
          <CustomAutocomplete
            id='combo-box-model'
            options={modelsTypes}
            getOptionLabel={type => type.name}
            renderInput={params => <TextField {...params} label='Ano' />}
            onChange={(_, value) => {
              setFipeModel({ ...fipeModel, model: value ?? undefined, vehicle_error: false });
            }}
            value={fipeModel.model ?? null}
            width={300}
          />
          <FormHelperText error>{fipeModel.model_error ? 'Ano obrigatório' : undefined}</FormHelperText>
        </>
      )}

      <TextField
        id='license-plate-field'
        label='Placa'
        value={fipeModel.license_plate}
        onChange={({ target }) => {
          handleChangeSelect('license_plate', target.value, true);
        }}
        error={fipeModel.license_plate_error}
        helperText={fipeModel.license_plate_error ? 'Placa inválida' : ''}
        inputProps={{ maxLength: 7 }}
      />

      <TextField
        id='vin-field'
        label='Chassi (opcional)'
        value={fipeModel.vin}
        onChange={({ target }) => {
          handleChangeSelect('vin', target.value, true);
        }}
      />

      <TextField
        id='color-field'
        label='Cor'
        value={fipeModel.color}
        onChange={({ target }) => {
          handleChangeSelect('color', target.value, true);
        }}
        error={fipeModel.color_error}
        helperText={fipeModel.color_error ? 'Cor obrigatória' : ''}
      />

      <CustomAutocomplete
        id='combo-box-state'
        options={states}
        getOptionLabel={state => state.acronym}
        renderInput={params => <TextField {...params} label='Estado' />}
        onChange={(_, value) => {
          handleChangeSelect('state', value?.acronym, true);
        }}
        value={states.find(item => item.acronym === fipeModel.state) ?? null}
        width={300}
      />
      <FormHelperText error>{fipeModel.model_error ? 'Estado obrigatório' : undefined}</FormHelperText>

      {loadingCities ? (
        <Loading />
      ) : (
        <CustomAutocomplete
          id='combo-box-city'
          options={cities}
          getOptionLabel={city => city.name}
          renderInput={params => <TextField {...params} label='Cidade' />}
          onChange={(_, value) => {
            handleChangeSelect('city', value?.name, true);
          }}
          value={cities.find(item => item.name === fipeModel.city) ?? null}
          width={300}
        />
      )}
      <FormHelperText error>{fipeModel.model_error ? 'Cidade obrigatório' : undefined}</FormHelperText>
    </div>
  );
}
